// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, Children, cloneElement } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import { CSSTransition } from 'react-transition-group';
import Checkbox from '../Checkbox';
import Dropdown from '../Dropdown';
import Spinner from '../Spinner';
import Translate from '../Translate';
import CardButton from './CardButton';
import PreviewPanel from './PreviewPanel';

import './Card.less';

const VIEW_TILE = 'tile';
const VIEW_EXTENDED = 'extended';
const VIEW_ROW = 'row';

/**
 * `Card` component.
 */
class Card extends Component {
    constructor(props) {
        super(props);

        this.state = {
            view: props.view,
        };
    }

    componentWillReceiveProps(nextProps) {
        const { view } = nextProps;

        if (view !== this.props.view) {
            this.setState({
                view,
            });
        }
    }

    renderSelection({ baseClassName, selection }) {
        if (!selection) {
            return null;
        }

        const { isSelected, onSelectionChange } = selection;

        return (
            <div className={`${baseClassName}__selection`}>
                <Checkbox
                    checked={isSelected}
                    onChange={isSelected => onSelectionChange({ isSelected })}
                >
                    {this.isExtended() ? <Translate content="Card.select" fallback="Select" /> : null}
                </Checkbox>
            </div>
        );
    }

    renderTitle({ baseClassName, title, titleLink }) {
        const titleLinkProps = typeof titleLink === 'string' ? { href: titleLink } : titleLink;

        return (
            <div className={`${baseClassName}__title`}>
                <span className={`${baseClassName}__title-text`}>{title}</span>
                {titleLinkProps && (
                    <CardButton
                        component="a"
                        icon="chain"
                        tooltip={<Translate content="Card.titleLinkTooltip" fallback="Go to page" />}
                        {...titleLinkProps}
                    />
                )}
            </div>
        );
    }

    renderLabels({ baseClassName, labels }) {
        if (labels.length === 0) {
            return null;
        }

        return (
            <div className={`${baseClassName}__labels`}>
                {Children.map(labels, label => cloneElement(label, {
                    className: classNames(`${baseClassName}__label`, label.props.className),
                }))}
            </div>
        );
    }

    renderHeader({ baseClassName, selection, title, titleLink, labels, rowContent, menu, buttons }) {
        return (
            <div className={`${baseClassName}__header`}>
                {this.renderSelection({ baseClassName, selection })}
                {this.renderTitle({ baseClassName, title, titleLink })}
                {this.isExtended() && this.renderLabels({ baseClassName, labels })}
                {this.renderButtons({ baseClassName, rowContent, menu, buttons })}
            </div>
        );
    }

    renderRowHeader({ baseClassName, subtitle, title, titleLink }) {
        return (
            <div className={`${baseClassName}__header`}>
                {subtitle && (
                    <div className={`${baseClassName}__subtitle`}>
                        {subtitle}
                    </div>
                )}
                {this.renderTitle({ baseClassName, title, titleLink })}
            </div>
        );
    }

    renderContent({ baseClassName, children }) {
        return (
            <div className={`${baseClassName}__main`}>
                <div className={`${baseClassName}__main-content`}>
                    {children}
                </div>
            </div>
        );
    }

    handleToggleView = () => {
        const { view } = this.state;

        this.setState({
            view: view === VIEW_EXTENDED ? VIEW_ROW : VIEW_EXTENDED,
        });
    };

    isExtended() {
        const { view } = this.state;

        return !this.isTile() && view === VIEW_EXTENDED;
    }

    isTile() {
        const { view } = this.state;

        return view === VIEW_TILE;
    }

    isRow() {
        const { view } = this.state;

        return !this.isTile() && view === VIEW_ROW;
    }

    renderButtons({ baseClassName, rowContent, menu, buttons }) {
        const result = [];

        if (!this.isRow()) {
            buttons.forEach((btn, index) => {
                result.push(<CardButton key={btn.key || index} {...btn} />);
            });
        }

        if (menu) {
            result.push(
                <Dropdown key="menu" menu={menu} menuPlacement={'bottom-end'}>
                    <CardButton icon="menu" />
                </Dropdown>
            );
        }

        if (rowContent && !this.isTile()) {
            result.push(
                <CardButton
                    key="toggleView"
                    icon={{ name: 'chevron-down', flipVertical: this.isExtended() }}
                    onClick={this.handleToggleView}
                    tooltip={
                        this.isExtended()
                            ? <Translate content="Card.collapseCardTooltip" fallback="Collapse the card" />
                            : <Translate content="Card.expandCardTooltip" fallback="Expand the card" />
                    }
                />
            );
        }

        if (result.length === 0) {
            return null;
        }

        return (
            <div className={`${baseClassName}__buttons`}>
                {result}
            </div>
        );
    }

    renderSideHeader({ sideHeader, baseClassName }) {
        if (!sideHeader) {
            return null;
        }

        let sideHeaderElement = sideHeader;
        if (sideHeader.type === PreviewPanel && this.isRow()) {
            sideHeaderElement = cloneElement(sideHeader, { children: null });
        }

        return (
            <div className={`${baseClassName}__side-header`}>
                {sideHeaderElement}
            </div>
        );
    }

    renderSideContent({ sideContent, baseClassName }) {
        if (!sideContent) {
            return null;
        }

        return (
            <div className={`${baseClassName}__side-content`}>
                {sideContent}
            </div>
        );
    }

    renderRow({ baseClassName, selection, rowContent, subtitle, title, titleLink, menu, sideHeader, buttons }) {
        return (
            <div className={`${baseClassName}__inner`}>
                <div className={`${baseClassName}__body`}>
                    {this.renderSelection({ baseClassName, selection })}
                    {this.renderSideHeader({ sideHeader, baseClassName })}
                    {this.renderRowHeader({ baseClassName, subtitle, title, titleLink })}
                    <div className={`${baseClassName}__main`}>
                        <div className={`${baseClassName}__main-content`}>
                            {rowContent}
                        </div>
                    </div>
                    {this.renderButtons({ baseClassName, rowContent, menu, buttons })}
                </div>
            </div>
        );
    }

    renderTile({ baseClassName, selection, title, titleLink, labels, children, rowContent, menu, sideHeader, sideContent, buttons }) {
        return (
            <div className={`${baseClassName}__inner`}>
                {this.renderHeader({ baseClassName, selection, title, titleLink, labels, rowContent, menu, buttons })}
                <div className={`${baseClassName}__body`}>
                    {this.renderSideHeader({ sideHeader, baseClassName })}
                    {this.renderContent({ baseClassName, children })}
                </div>
                {this.renderSideContent({ sideContent, baseClassName })}
            </div>
        );
    }

    renderExtended({ baseClassName, selection, title, titleLink, labels, children, rowContent, menu, sideHeader, sideContent, buttons }) {
        const sideHeaderElement = this.renderSideHeader({ sideHeader, baseClassName });
        const sideContentElement = this.renderSideContent({ sideContent, baseClassName });

        return (
            <div className={`${baseClassName}__inner`}>
                {this.renderHeader({ baseClassName, selection, title, titleLink, labels, rowContent, menu, buttons })}

                <div className={`${baseClassName}__body`}>
                    {sideHeaderElement || sideContentElement
                        ? (
                            <div className={`${baseClassName}__side`}>
                                {sideHeaderElement}
                                {sideContentElement}
                            </div>
                        )
                        : null}
                    {this.renderContent({ baseClassName, children })}
                </div>
            </div>
        );
    }

    renderLoader({ baseClassName, progress }) {
        if (!progress) {
            return null;
        }

        return (
            <div className={`${baseClassName}__content-blocker`}>
                <div className={`${baseClassName}__loader`}>
                    <Spinner className={`${baseClassName}__loader-spinner`} />
                    {progress === true ? null : <div className={`${baseClassName}__loader-text`}>{progress}</div>}
                </div>
            </div>
        );
    }

    render() {
        const {
            baseClassName,
            className,
            children,
            view,
            intent,
            title,
            subtitle,
            titleLink,
            selection,
            rowContent,
            sideHeader,
            sideContent,
            buttons,
            menu,
            labels,
            progress,
            ...props
        } = this.props;

        return (
            <CSSTransition
                classNames={`${baseClassName}-`}
                in={this.isExtended()}
                timeout={300}
            >
                <div
                    className={classNames(
                        baseClassName, {
                            [`${baseClassName}--extended`]: this.isExtended(),
                            [`${baseClassName}--tile`]: this.isTile(),
                            [`${baseClassName}--row`]: this.isRow(),
                            [`${baseClassName}--${intent}`]: intent,
                            [`${baseClassName}--progress`]: progress,
                        }, className)
                    }
                    {...props}
                >
                    {this.isExtended() && this.renderExtended({ baseClassName, selection, title, titleLink, labels, children, rowContent, menu, sideHeader, sideContent, buttons })}
                    {this.isTile() && this.renderTile({ baseClassName, selection, title, titleLink, labels, children, rowContent, menu, sideHeader, sideContent, buttons })}
                    {this.isRow() && this.renderRow({ baseClassName, selection, rowContent, subtitle, title, titleLink, menu, sideHeader, buttons })}
                    {this.renderLoader({ baseClassName, progress })}
                </div>
            </CSSTransition>
        );
    }
}
Card.propTypes = {
    /**
     * Content of the `Card`.
     */
    children: PropTypes.node,
    /**
     * View mode.
     */
    view: PropTypes.oneOf([VIEW_EXTENDED, VIEW_TILE, VIEW_ROW]),
    /**
     * Visual intent color to apply to component.
     */
    intent: PropTypes.oneOf(['warning', 'danger', 'inactive']),
    /**
     * Text of title.
     */
    title: PropTypes.node.isRequired,
    /**
     * Link for title.
     */
    titleLink: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    /**
     * Text of subtitle.
     */
    subtitle: PropTypes.node,
    /**
     * List of labels in header. See [Label](#!/Label) component for details.
     */
    labels: PropTypes.arrayOf(PropTypes.element),
    /**
     * List of additional buttons in header.
     */
    buttons: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object,
            PropTypes.element,
        ]).isRequired,
        onClick: PropTypes.func.isRequired,
        tooltip: PropTypes.node,
    })),
    /**
     * See [Menu](#!/Menu).
     */
    menu: PropTypes.element,
    /**
     * Selection control.
     */
    selection: PropTypes.shape({
        isSelected: PropTypes.bool.isRequired,
        onSelectionChange: PropTypes.func.isRequired,
    }),
    /**
     * Side header section. Recommended to use [PreviewPanel](#!/PreviewPanel) component.
     */
    sideHeader: PropTypes.element,
    /**
     * Side content section.
     */
    sideContent: PropTypes.element,
    /**
     * Content for row view.
     */
    rowContent: PropTypes.any,
    /**
     * Indicate the progress state of the card.
     */
    progress: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.node,
    ]),
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};
Card.defaultProps = {
    children: null,
    view: VIEW_EXTENDED,
    intent: null,
    subtitle: '',
    titleLink: null,
    labels: [],
    buttons: [],
    menu: null,
    selection: null,
    sideHeader: null,
    sideContent: null,
    rowContent: null,
    progress: false,
    className: null,
    baseClassName: `${CLS_PREFIX}card`,
};

export default Card;



// WEBPACK FOOTER //
// ./components/Card/Card.js